import { useEffect, useMemo, useState } from 'react';
import { IconChevronDown, IconChevronUp } from '@tabler/icons-react';

import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import {
  Box,
  Collapse,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useLocation, useNavigate } from 'react-router-dom';

import { NavigationListItemButtonStyled } from '@kit/navigation';
import { NavigationItem } from '@kit/navigation/navigation-item';

import {
  navigationCollapseIconStyle,
  NavigationCollapseListStyled,
} from './styled';
import { NavCollapseType } from './types';

export const NavigationCollapse: NavCollapseType = (props) => {
  const { menu, level } = props;

  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const isMenuActive = Boolean(
    menu.url &&
      (location.pathname === menu.url ||
        location.pathname.startsWith(`${menu.url}/`))
  );
  const isMenuSelected = location.pathname === menu.url;
  const [open, setOpen] = useState(isMenuActive);
  const isSelected = isMenuSelected || (!open && isMenuActive);

  useEffect(() => {
    if (!isMenuActive) {
      return;
    }

    setOpen(true);
  }, [isMenuActive]);

  const handleNavigate = () => {
    setOpen(true);

    if (menu.url) {
      navigate(menu.url);
    }
  };

  const handleToggle = () => {
    setOpen((currentOpen) => !currentOpen);
  };

  const menus = useMemo(
    () =>
      menu.children?.map((item) => {
        switch (item.type) {
          case 'collapse':
            return (
              <NavigationCollapse key={item.id} menu={item} level={level + 1} />
            );
          case 'item':
            return (
              <NavigationItem key={item.id} item={item} level={level + 1} />
            );
          default:
            return (
              <Typography
                key={item.id}
                variant="h6"
                color="error"
                align="center"
              >
                Menu Items Error
              </Typography>
            );
        }
      }),
    [level, menu.children]
  );

  const Icon = menu.icon!;
  const menuIcon = menu.icon ? (
    <Icon
      strokeWidth={1.5}
      width="1.3rem"
      height="1.3rem"
      style={navigationCollapseIconStyle}
    />
  ) : (
    <FiberManualRecordIcon
      sx={{
        width: isSelected ? 8 : 6,
        height: isSelected ? 8 : 6,
      }}
      fontSize={level > 0 ? 'inherit' : 'medium'}
    />
  );

  return (
    <>
      <NavigationListItemButtonStyled
        level={level} //
        selected={isSelected}
        onClick={handleNavigate}
      >
        <ListItemIcon sx={{ my: 'auto', minWidth: !menu.icon ? 18 : 36 }}>
          {menuIcon}
        </ListItemIcon>
        <ListItemText
          primary={
            <Typography
              variant={isSelected ? 'h5' : 'body1'}
              color="inherit"
              sx={{ my: 'auto' }}
            >
              {menu.title}
            </Typography>
          }
          secondary={
            menu.caption && (
              <Typography
                variant="caption"
                sx={{ ...theme.typography.subMenuCaption }}
                display="block"
                gutterBottom
              >
                {menu.caption}
              </Typography>
            )
          }
        />
        <Box
          component="span"
          role="button"
          tabIndex={0}
          aria-label={open ? 'Collapse submenu' : 'Expand submenu'}
          onClick={(event) => {
            event.stopPropagation();
            handleToggle();
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              event.stopPropagation();
              handleToggle();
            }
          }}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: 1.5,
            py: 0.5,
            ml: 1,
            borderRadius: 1,
            color: 'inherit',
            cursor: 'pointer',
            '&:hover, &:focus-visible': {
              backgroundColor: 'action.selected',
            },
          }}
        >
          {open ? (
            <IconChevronUp stroke={1.5} size={16} />
          ) : (
            <IconChevronDown stroke={1.5} size={16} />
          )}
        </Box>
      </NavigationListItemButtonStyled>

      <Collapse in={open} timeout="auto" unmountOnExit>
        <NavigationCollapseListStyled component="div" disablePadding>
          {menus}
        </NavigationCollapseListStyled>
      </Collapse>
    </>
  );
};
